import { createSlice } from '@reduxjs/toolkit'

import { history, useSelector } from 'domains/application/hooks'
import appSlice from 'domains/application/slice'
import { AppDispatch, AppGetState } from 'domains/application/store'
import { User, UUID7 } from 'domains/organization/data-types'

type BinaryId = string

interface AuthUser {
  id: BinaryId
  email: string
}

interface State {
  authUser: AuthUser | null
  user: User | null
  fetchingRegistration: boolean
  registrationErrors: Record<string, string> | null
  registration: {
    email: string
    password: string
  }
  login: {
    email: string
    password: string
  }
  loginErrors: Record<string, string> | null
  loginSuccess: boolean
  fetchingLogin: boolean
  confirmationEmailRequested: number | null
  confirmationEmailSent: boolean
  confirmationEmailError: 'rate-limit' | 'other' | null
  emailInstructionsByUserId: Record<
    UUID7,
    { lastSent: number; lastRequested: number }
  >
}

const slice = createSlice({
  name: 'auth',
  initialState: {
    fetchingLogin: false,
    fetchingRegistration: false,
    registration: {
      email: '',
      password: '',
    },
    login: {
      email: '',
      password: '',
    },
    loginErrors: null,
    loginSuccess: false,
    registrationErrors: null,
    confirmationEmailRequested: null,
    confirmationEmailSent: false,
    confirmationEmailError: null,
    emailInstructionsByUserId: {},
  } as State,
  reducers: {
    updateRegistration(
      state,
      { payload }: { payload: { email?: string; password?: string } },
    ) {
      state.registration = {
        ...state.registration,
        ...payload,
      }
      state.registrationErrors = null
    },
    registerRequest(state) {
      state.fetchingRegistration = true
    },
    registerReceived(state, _action: { payload: { authUser: AuthUser } }) {
      state.fetchingRegistration = false
      state.registration = { email: '', password: '' }
    },
    registerFailed(
      state,
      action: { payload: { errors: Record<string, string> | null } },
    ) {
      state.fetchingRegistration = false
      state.authUser = null
      state.registrationErrors = action.payload.errors
    },
    updateLogin(
      state,
      { payload }: { payload: { email?: string; password?: string } },
    ) {
      state.login = {
        ...state.login,
        ...payload,
      }
      state.loginErrors = null
    },
    loginRequest(state) {
      state.fetchingLogin = true
    },
    loginReceived(
      state,
      action: { payload?: { auth_user: AuthUser; user: User } },
    ) {
      state.authUser = action.payload?.auth_user ?? null
      state.user = action.payload?.user ?? null
      state.fetchingLogin = false
      state.login = { email: '', password: '' }
      state.loginSuccess = true
    },
    loginFailed(
      state,
      action: { payload: { errors: Record<string, string> | null } },
    ) {
      state.fetchingLogin = false
      state.authUser = null
      state.loginErrors = action.payload.errors
    },
    requestConfirmationEmailResend(
      state,
      _action: { payload?: Record<string, never> },
    ) {
      state.confirmationEmailRequested = Date.now()
    },
    receiveConfirmationEmailResend(
      state,
      action: { payload: { error?: 'rate-limit' | 'other' } },
    ) {
      if (action.payload.error != null) {
        state.confirmationEmailSent = true
        state.confirmationEmailError = null
      } else {
        state.confirmationEmailError = action.payload.error ?? null
      }
    },
    'sendNewLoginInstructionsEmail/request': (
      state: State,
      action: { payload: { userId: UUID7 } },
    ) => {
      const current = state.emailInstructionsByUserId[action.payload.userId]
      state.emailInstructionsByUserId[action.payload.userId] = {
        ...(current ?? {}),
        lastRequested: Date.now(),
      }
    },
    'sendNewLoginInstructionsEmail/receive': (
      state: State,
      action: { payload: { userId: UUID7 } },
    ) => {
      state.emailInstructionsByUserId[action.payload.userId] = {
        ...(state.emailInstructionsByUserId[action.payload.userId] ?? {}),
        lastSent: Date.now(),
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(appSlice.actions.setPath, (state, _action) => {
      state.registration = { email: '', password: '' }
      state.registrationErrors = null
      state.login = { email: '', password: '' }
      state.loginErrors = null
      state.loginSuccess = false
    })
  },
})

export const register =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    dispatch(slice.actions.registerRequest())
    const registration = getState().auth.registration

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registration),
      })
      const { data, errors } = await response.json()

      if (response.status >= 400) {
        return dispatch(slice.actions.registerFailed({ errors }))
      }

      dispatch(slice.actions.registerReceived({ authUser: data.auth_user }))
      history?.push('/login')
      dispatch(
        appSlice.actions.putFlash({ flash: { confirm: 'Check your email' } }),
      )
    } catch (error) {
      dispatch(
        slice.actions.registerFailed({ errors: { form: String(error) } }),
      )
    }
  }

export const login =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    dispatch(slice.actions.loginRequest())
    const login = getState().auth.login

    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(login),
      })
      const { data, errors, flash } = await response.json()

      if (response.status >= 400) {
        return dispatch(slice.actions.loginFailed({ errors }))
      }

      dispatch(slice.actions.loginReceived(data))

      // There was no existing user and no invite for this user existed so no user was returned
      if (data.user == null || data.auth_user?.confirmed_at == null) {
        window.location.reload()
      } else {
        history?.push('/app')
      }
      dispatch(appSlice.actions.putFlash({ flash: flash ?? {} }))
    } catch (error) {
      dispatch(slice.actions.loginFailed({ errors: { form: String(error) } }))
    }
  }

export const resendConfirmationEmail =
  () => async (dispatch: AppDispatch, _getState: AppGetState) => {
    dispatch(slice.actions.requestConfirmationEmailResend())
    const response = await fetch('/api/login/resend-confirmation', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const { error } = await response.json()
    dispatch(slice.actions.receiveConfirmationEmailResend({ error }))
  }

export const sendLoginInstructions =
  (id: User['id']) => async (dispatch: AppDispatch) => {
    try {
      dispatch(
        slice.actions['sendNewLoginInstructionsEmail/request']({
          userId: id,
        }),
      )

      const response = await fetch(
        `/api/users/${id}/send-new-login-email-instructions`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )

      if (response.status > 299) {
        return
      }

      dispatch(
        slice.actions['sendNewLoginInstructionsEmail/receive']({
          userId: id,
        }),
      )
    } catch (err) {
      console.error(err)
      // noop
    }
  }

export const useCurrentUser = () => {
  return useSelector((state) => state.auth.user)
}

export default slice
